import React, { useCallback, useEffect, useState } from 'react'

// redux
import { useDispatch } from 'react-redux'

// components
import OfferTable from 'components/Table/OfferTable'
import OfferAccordion from 'components/Accordion/OfferAccordion'
import Alert from 'components/Alert'

// custom functions
import { useViewport } from 'utils'
import { useAuthentication } from 'hooks/useAuthentication'
import { getPriceValue } from 'utils/strings'

// solana
import { useMetaplex } from 'hooks/useMetaplex'
import { getMyBids, getReceivedBids, cancelOffer, acceptOffer, LazyBidWithAsset } from 'utils/auction.util'

// call apis
import listingsAPI from 'apis/listings'
import NoDataSection from '../NoDataSection'
import usersAPI from 'apis/user'
import LoadingComponent from 'pages/explore/LoadingComponent'

// styles
import styles from './index.module.scss'

const OfferSection = props => {
  const { shownOfferType, offerNftsMade, setOfferNftsMade, offerNftsReceived, setOfferNftsReceived } = props
  const viewport = useViewport()
  const { refreshToken } = useAuthentication()

  const [offersMade, setOffersMade] = useState<LazyBidWithAsset[] | null>(null)
  const [offersReceived, setOffersReceived] = useState<LazyBidWithAsset[] | null>(null)

  const [shownOfferList, setShownOfferList] = useState<LazyBidWithAsset[]>([])
  const [shownOfferListChunk, setShownOfferListChunk] = useState<LazyBidWithAsset[]>([])

  const [showAlert, setShowAlert] = useState(false)
  const [alertData, setAlertData] = useState<any[]>([])
  const [offerLoaded, setOfferLoaded] = useState(false)

  const [rowCount, setRowCount] = useState(10)
  const [isFetching, setIsFetching] = useState(false)
  const [isVisibleFetching, setIsVisibleFetching] = useState(false)

  const dispatch = useDispatch()

  const { metaplex: mx } = useMetaplex()

  const fetchOffersMade = useCallback(
    async (force: boolean = false) => {
      if (!mx || (offersMade && !force)) return
      let offers = await getMyBids(mx)
      setOffersMade(offers)
      setOfferNftsMade(offers)
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [mx, offersMade]
  )

  const fetchOffersReceived = useCallback(
    async (force: boolean = false) => {
      if (!mx || (offersReceived && !force)) return
      let received = await getReceivedBids(mx)
      let received_reverse = received.reverse()
      setOffersReceived(received_reverse)
      setOfferNftsReceived(received_reverse)
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [mx, offersReceived]
  )

  useEffect(() => {
    ;(async () => {
      setOfferLoaded(false)
      try {
        if (shownOfferType === 'made') {
          if (offerNftsMade.length > 0) {
            setOffersMade(offerNftsMade)
          } else await fetchOffersMade()
        } else if (shownOfferType === 'received') {
          if (offerNftsReceived.length > 0) {
            setOffersReceived(offerNftsReceived)
          } else await fetchOffersReceived()
        }
      } catch (e) {
        console.error(e)
      }
      setOfferLoaded(true)
    })()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shownOfferType, fetchOffersMade, fetchOffersReceived])

  useEffect(() => {
    if (shownOfferType === 'made') setShownOfferList(offersMade ?? [])
    else if (shownOfferType === 'received') setShownOfferList(offersReceived ?? [])
  }, [shownOfferType, offersMade, offersReceived])

  useEffect(() => {
    setShownOfferListChunk(shownOfferList.slice(0, rowCount))
    if (shownOfferList.length > rowCount) setIsVisibleFetching(true)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shownOfferList])

  useEffect(() => {
    if (isFetching) {
      setIsFetching(false)
      if (shownOfferListChunk.length + rowCount > shownOfferList.length) {
        setShownOfferListChunk([
          ...shownOfferListChunk,
          ...shownOfferList.slice(shownOfferListChunk.length, shownOfferList.length),
        ])
        setIsVisibleFetching(false)
      } else {
        setShownOfferListChunk([
          ...shownOfferListChunk,
          ...shownOfferList.slice(shownOfferListChunk.length, shownOfferListChunk.length + rowCount),
        ])
        setIsVisibleFetching(true)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isFetching])

  const onCancel = async (listing: LazyBidWithAsset) => {
    await refreshToken()

    const { tradeStateAddress, asset } = listing
    const result = await cancelOffer(mx, tradeStateAddress)
    if (result) {
      listingsAPI.deleteOffer(asset.mintAddress.toBase58()).then(res => {
        if (res) {
          fetchOffersMade(true)
          setAlertData([
            {
              id: 0,
              title: 'Your offer has been withdrawn',
              description: `You've successfully canceled your offer on ${listing.asset.name}`,
              type: 'success',
            },
          ])
          setShowAlert(true)

          usersAPI.getLockupValues().then(
            response => {
              dispatch({ type: 'set', bidWalletLock: response.bid_wallet_lock })
              dispatch({ type: 'set', offerWalletLock: response.offer_wallet_lock })
            },
            error => {
              console.log(error)
            }
          )
        }
      })
    }
  }

  const onAccept = async (bid: LazyBidWithAsset) => {
    await refreshToken()

    const result = await acceptOffer(mx, bid)
    if (result) {
      setAlertData([
        {
          id: 0,
          title: 'Offer successfully accepted',
          description: `You've successfully accepted an offer for ${bid.asset.name}`,
          type: 'success',
        },
      ])
      setShowAlert(true)

      setOfferLoaded(false)
      await fetchOffersReceived(true)
      setOfferLoaded(true)

      listingsAPI
        .acceptOffer(bid.asset.mintAddress.toBase58(), {
          buyer_wallet_id: bid.buyerAddress.toBase58(),
          amount: getPriceValue(bid.price.basisPoints.toNumber()),
        })
        .then(response => {
          usersAPI.getLockupValues().then(
            response => {
              dispatch({ type: 'set', bidWalletLock: response.bid_wallet_lock })
              dispatch({ type: 'set', offerWalletLock: response.offer_wallet_lock })
            },
            error => {
              console.log(error)
            }
          )
        }),
        error => {
          console.log(error)
        }
    } else {
      setAlertData([
        {
          id: 0,
          title: 'Offer accept failed!',
          description: `Failed to send the transaction, please check the validation for ${bid.asset.name}`,
          type: 'failure',
        },
      ])
      setShowAlert(true)
    }
  }

  return (
    <section className="w-full mt-[16px] lg:mt-[0px]">
      {!offerLoaded ? (
        <div className="w-full">
          {Array.from({ length: 6 }).map((_, index) => (
            <div key={index} className="w-full mt-[20px] px-[15px]">
              <div className="w-full flex gap-[15px]">
                <div className="min-w-[70px] max-w-[70px] h-[70px] rounded-[10px] overflow-hidden">
                  <div className="skeletonLoader"></div>
                </div>
                <div className="w-full flex justify-between items-center">
                  <div className="w-full">
                    <div className="w-full lg:w-1/2 h-[16px] rounded-[8px] overflow-hidden">
                      <div className="skeletonLoader"></div>
                    </div>
                    <div className="w-1/2 lg:w-1/4 h-[16px] rounded-[8px] overflow-hidden mt-[15px]">
                      <div className="skeletonLoader"></div>
                    </div>
                  </div>
                  <div className="hidden lg:block w-[100px] h-[16px] rounded-[8px] overflow-hidden">
                    <div className="skeletonLoader"></div>
                  </div>
                </div>
              </div>
              <div className={styles.border}></div>
            </div>
          ))}
        </div>
      ) : shownOfferListChunk.length > 0 ? (
        <div className="w-full mt-[0px] lg:mt-[0px]">
          {viewport === 'lg' || viewport === 'xl' || viewport === '2xl' ? (
            <OfferTable
              offerType={shownOfferType}
              data={shownOfferListChunk}
              onCancel={tradeStateAddress => onCancel(tradeStateAddress)}
              onAccept={onAccept}
            />
          ) : (
            <OfferAccordion
              offerType={shownOfferType}
              data={shownOfferListChunk}
              onCancel={tradeStateAddress => onCancel(tradeStateAddress)}
              onAccept={onAccept}
            />
          )}
        </div>
      ) : (
        <div className="mt-[70px] lg:mt-[150px]">
          <NoDataSection title="" description="No offers found" />
        </div>
      )}

      {isVisibleFetching && (
        <div className="w-full mt-[100px] mb-[40px]">
          <LoadingComponent isFetching={isFetching} setIsFetching={setIsFetching} text="Loading offers" />
        </div>
      )}

      <Alert
        open={showAlert}
        close={() => {
          setShowAlert(false)
        }}
        data={alertData}
      />
    </section>
  )
}

export default OfferSection
